/**
 * Building kit registry: every structure module, trim piece, and greeble is a
 * committed `buildingModule` procedural-asset doc, compiled client-side by
 * compileAsset and instanced by unique geometry. Palette convention (FIXED):
 * material 0 = plaster/plating (per-instance tint), 1 = metal trim,
 * 2 = rust-orange accent, 3 = glass (night-emissive client path).
 */
import type { AssetModifier, AssetNode, AssetVec3, ProceduralAssetV1 } from "../proceduralAssets.js";

/** Module grid unit / storey height. 3 → 4 m (2026-07-28 interiors scale-up):
 * a 4 m storey leaves ~3.6 m clear headroom over a 0.4 m floor — the vault
 * theme's proven VAULT_HEADROOM_MIN, the bar the third-person camera boom
 * needs inside enclosed rooms. NOT wire-bound (unlike homestead PIECE_UNIT_M,
 * which deliberately stays 3): changing these regenerates every settlement
 * consistently on both client and server. */
export const MODULE_UNIT_M = 4;
export const LEVEL_H_M = 4;

// Palette base albedos. Material 0 (plaster) was a pale sand (0xcfc4ae, luma ~0.78)
// that read BRIGHTER than the packed-earth roads and floated above the darker,
// warmer terrain. It is now a weathered warm-adobe (0xa89173, luma ~0.58) that
// sits in the world's value band — between the road tan (0xc7baa3) and the old
// warm-ochre buildings (0x9e7854) — and gives the per-instance ramp/age tints a
// mid-value base to swing around (buildingMeshes.ts). Metal trim darkened toward
// the cliff grey (0x5b544c) for the same reason. Accent (2) and glass (3) are
// FIXED by convention (pinned in buildings-kit.test.mts) — do not retint here.
const PALETTE = [
  { color: 0xa89173, roughness: 0.9, metalness: 0.0 },   // 0 plaster/plating (walls)
  { color: 0x6b6259, roughness: 0.6, metalness: 0.4 },   // 1 metal trim
  { color: 0xb35c1e, roughness: 0.8, metalness: 0.1 },   // 2 rust-orange accent
  { color: 0x223038, roughness: 0.2, metalness: 0.0 },   // 3 glass (night-emissive)
  // 4 dark roof slate — the VALUE CONTRAST that stops a building reading as one
  // solid tan "sandcastle" block: tan walls under a distinctly darker roof, the
  // way the legacy buildings had a 0x5a5049 slate roof over white walls. Warmed
  // toward the world's earth tones rather than a cold grey.
  { color: 0x4a4038, roughness: 0.85, metalness: 0.05 }, // 4 roof
];

export interface KitEntry {
  id: string;
  role: "structure" | "trim" | "greeble";
  doc: ProceduralAssetV1;
  footprintUnits: [number, number];
}

let nextId = 0;
const nid = (): string => `n${nextId++}`;
const box = (params: [number, number, number], p: AssetVec3,
  material: number, r: AssetVec3 = [0, 0, 0]): AssetNode =>
  ({ id: nid(), kind: "box", params, p, r, material });
const cyl = (radius: number, height: number, p: AssetVec3, material: number,
  r: AssetVec3 = [0, 0, 0]): AssetNode =>
  ({ id: nid(), kind: "cylinder", params: [radius, radius, height], p, r, material });
const cone = (radius: number, height: number, p: AssetVec3, material: number,
  r: AssetVec3 = [0, 0, 0]): AssetNode =>
  ({ id: nid(), kind: "cone", params: [radius, height], p, r, material });
const sphere = (radius: number, p: AssetVec3, material: number): AssetNode =>
  ({ id: nid(), kind: "sphere", params: [radius], p, r: [0, 0, 0], material });
const socket = (name: string, p: AssetVec3): AssetNode =>
  ({ id: nid(), name, kind: "socket", p, r: [0, 0, 0] });
const mods = (node: AssetNode, modifiers: AssetModifier[]): AssetNode => ({ ...node, modifiers });

/** Scope node ids per doc: sequential renumber so ids are unique within each
 * doc regardless of when nid() was called (fixes the stale-counter collision
 * where argument-built `extra` nodes predate the factory's own reset). */
function renumber(nodes: AssetNode[], counter = { i: 0 }): void {
  for (const n of nodes) {
    n.id = `n${counter.i++}`;
    if (n.children) renumber(n.children, counter);
  }
}

function doc(id: string, name: string, footprintUnits: [number, number],
  role: KitEntry["role"], nodes: AssetNode[],
  materials: typeof PALETTE = PALETTE): KitEntry {
  renumber(nodes);
  return {
    id, role, footprintUnits,
    doc: {
      version: 1, id, name, kind: "buildingModule", tags: ["building-kit"],
      materials, nodes, buildingModule: { footprintUnits },
    },
  };
}

/** wU×dU-unit plating box module, one level tall, with roof + 4 wall sockets. */
function structureBox(id: string, name: string, wU: number, dU: number,
  extra: AssetNode[] = [], mainMods?: AssetModifier[]): KitEntry {
  nextId = 0;
  const w = wU * MODULE_UNIT_M, d = dU * MODULE_UNIT_M, h = LEVEL_H_M;
  const plating = box([w - 0.1, h, d - 0.1], [0, h / 2, 0], 0);
  return doc(id, name, [wU, dU], "structure", [
    // `mainMods` deform only the main plating box — the roof cap stays true
    mainMods ? mods(plating, mainMods) : plating,
    // dark roof cap (material 4) with a slight eaves overhang. Its TOP sits a hair
    // ABOVE the plating top (h) and its footprint fully covers it, so the plating's
    // tan top face is occluded rather than COPLANAR with the cap — coplanar faces
    // z-fight (the "two overlapping pieces" artifact). Walls read tan, roof dark
    // (the sandcastle fix); on a stacked module it becomes a dark floor/eaves band.
    box([w + 0.12, 0.2, d + 0.12], [0, h - 0.04, 0], 4),
    socket("roof:c", [0, h, 0]),
    socket("wall:n", [0, h * 0.5, d / 2]), socket("wall:s", [0, h * 0.5, -d / 2]),
    socket("wall:e", [w / 2, h * 0.5, 0]), socket("wall:w", [-w / 2, h * 0.5, 0]),
    socket("ground:n", [0, 0, d / 2 + 0.9]),
    ...extra,
  ]);
}

/** Seamless-tier shell: four thin wall panels (+z split around a door gap),
 * a floor slab, and the standard structure socket set. */
function doorHollow(): KitEntry {
  nextId = 0;
  const w = MODULE_UNIT_M, d = MODULE_UNIT_M, h = LEVEL_H_M, t = 0.15;
  const flank = (w - 1.2) / 2; // width of each +z panel flanking the 1.2 m door
  return doc("mod-box-door-hollow", "Hollow Door Module", [1, 1], "structure", [
    box([flank, h, t], [1.2 / 2 + flank / 2, h / 2, d / 2], 0),  // +z right of door
    box([flank, h, t], [-(1.2 / 2 + flank / 2), h / 2, d / 2], 0), // +z left of door
    box([w, h, t], [0, h / 2, -d / 2], 0),                        // -z wall
    box([t, h, d], [w / 2, h / 2, 0], 0),                         // +x wall
    box([t, h, d], [-w / 2, h / 2, 0], 0),                        // -x wall
    box([w, t, d], [0, t / 2, 0], 1),                             // floor slab
    box([w + 0.12, 0.2, d + 0.12], [0, h - 0.04, 0], 4),          // dark roof cap (top clears walls)
    socket("roof:c", [0, h, 0]),
    socket("wall:n", [0, h * 0.5, d / 2]), socket("wall:s", [0, h * 0.5, -d / 2]),
    socket("wall:e", [w / 2, h * 0.5, 0]), socket("wall:w", [-w / 2, h * 0.5, 0]),
    socket("ground:n", [0, 0, d / 2 + 0.9]),
  ]);
}

/** Tapered upper-storey shaft (0.8× inset footprint), no ground socket. */
function towerShaft(): KitEntry {
  nextId = 0;
  const w = MODULE_UNIT_M * 0.8, d = MODULE_UNIT_M * 0.8, h = LEVEL_H_M;
  return doc("mod-tower-shaft", "Tower Shaft", [1, 1], "structure", [
    mods(box([w, h, d], [0, h / 2, 0], 0), [{ type: "taper", axis: "y", factor: 0.85 }]),
    box([w + 0.2, 0.2, d + 0.2], [0, h - 0.04, 0], 4), // dark roof cap (top clears plating)
    socket("roof:c", [0, h, 0]),
    socket("wall:n", [0, h * 0.5, d / 2]), socket("wall:s", [0, h * 0.5, -d / 2]),
    socket("wall:e", [w / 2, h * 0.5, 0]), socket("wall:w", [-w / 2, h * 0.5, 0]),
  ]);
}

/** Overhanging tower crown with a window band radially arrayed onto all 4 faces.
 * Proportional to the module: crown overhangs the cell by 0.8 m (the 3.8-on-3
 * read), height ~0.87 of a storey. */
function towerHead(): KitEntry {
  nextId = 0;
  const w = MODULE_UNIT_M + 0.8, h = LEVEL_H_M * 0.87;
  return doc("mod-tower-head", "Tower Head", [1, 1], "structure", [
    box([w, h, w], [0, h / 2, 0], 0),
    mods(box([w - 1.2, 0.9, 0.12], [0, h / 2, w / 2 + 0.06], 3),
      [{ type: "array", mode: "radial", count: 4, axis: "y", radius: 0 }]),
    box([w + 0.24, 0.24, w + 0.24], [0, h - 0.06, 0], 4), // dark crown roof (top clears crown)
    socket("roof:c", [0, h, 0]),
  ]);
}

/** Dome cap — bottom hemisphere buried in the module below (r = 0.7×unit). */
function dome(): KitEntry {
  nextId = 0;
  return doc("mod-dome", "Dome Cap", [1, 1], "structure", [
    sphere(MODULE_UNIT_M * 0.7, [0, MODULE_UNIT_M / 3, 0], 0),
    socket("roof:c", [0, LEVEL_H_M, 0]),
  ]);
}

// ── interiors P1: hollow ground-floor shell pieces ────────────────────────────
// One piece per plan.ts ShellWall/floor/cap entry. All are authored on the +z
// CELL edge (pre-offset like trim-parapet-edge) and anchored at the CELL
// centre with rot = SIDE_ROT[side]; dimensions mirror the plan's shell
// collision constants (SHELL_WALL_T 0.4 etc. — kept literal here to avoid a
// kit→plan import cycle; pinned equal in buildings-kit.test.mts).
const SHELL_T = 0.4;

/** Solid full-height wall panel on the +z cell edge, inset inside the footprint. */
function shellWall(id: string, name: string, extra: AssetNode[] = []): KitEntry {
  nextId = 0;
  const U = MODULE_UNIT_M, H = LEVEL_H_M;
  return doc(id, name, [1, 1], "structure", [
    box([U, H, SHELL_T], [0, H / 2, U / 2 - SHELL_T / 2], 0),
    ...extra,
  ]);
}

/** Window wall — a REAL OPENING (2026-07-30 play report: "windows are just
 *  glass panels on the wall, they don't look outside"): sill, header and
 *  flank panels around a 2.4 × 1.2 m hole, with ONE transparent pane at the
 *  wall centre plane. Frame faces keep the off-plane rule (no face on the
 *  flank/sill/header boundary planes). Collision stays the FULL solid wall
 *  (plan.ts) — window glass is armored; only the light gets through. */
function shellWallWin(): KitEntry {
  nextId = 0;
  const U = MODULE_UNIT_M, H = LEVEL_H_M;
  const winW = U * 0.6, winB = H * 0.48, winT = H * 0.78; // 2.4 wide, y 1.92..3.12
  const flankW = (U - winW) / 2, ez = U / 2 - SHELL_T / 2;
  return doc("shell-wall-win", "Shell Window Wall", [1, 1], "structure", [
    box([U, winB, SHELL_T], [0, winB / 2, ez], 0),                              // sill panel
    box([U, H - winT, SHELL_T], [0, (H + winT) / 2, ez], 0),                    // header panel
    box([flankW, winT - winB, SHELL_T], [-(winW / 2 + flankW / 2), (winB + winT) / 2, ez], 0),
    box([flankW, winT - winB, SHELL_T], [(winW / 2 + flankW / 2), (winB + winT) / 2, ez], 0),
    box([winW, winT - winB, 0.05], [0, (winB + winT) / 2, ez], 3),              // the pane
    // metal frame, exterior-proud, CROSSING the opening boundary planes so no
    // face lands on them (rails centred ON the sill/header planes; verticals
    // 0.02 clear of the flank planes) — the P3.7 off-plane rule.
    box([winW + 0.2, 0.12, SHELL_T], [0, winB, ez + 0.05], 1),
    box([winW + 0.2, 0.12, SHELL_T], [0, winT, ez + 0.05], 1),
    box([0.1, winT - winB + 0.12, SHELL_T], [-(winW / 2 + 0.03), (winB + winT) / 2, ez + 0.05], 1),
    box([0.1, winT - winB + 0.12, SHELL_T], [(winW / 2 + 0.03), (winB + winT) / 2, ez + 0.05], 1),
  ]);
}

/** Doorway wall: two flanks + a header around the opening. P2 fix pass: gap
 *  1.2 → 1.6 m wide (entry felt sticky at 0.15 m of side clearance) and the
 *  header bottom FIXED at 3.2 m — matching plan.ts SHELL_DOOR_W /
 *  SHELL_DOOR_HEAD_M exactly, so this one authored piece agrees with the
 *  collision header across every per-building floor raise (the sill is the
 *  floor slab's edge, not part of this piece). */
function shellWallDoor(): KitEntry {
  nextId = 0;
  const U = MODULE_UNIT_M, H = LEVEL_H_M;
  const doorW = 1.6, headB = 3.2;
  const flank = (U - doorW) / 2, off = doorW / 2 + flank / 2, ez = U / 2 - SHELL_T / 2;
  return doc("shell-wall-door", "Shell Doorway Wall", [1, 1], "structure", [
    box([flank, H, SHELL_T], [-off, H / 2, ez], 0),
    box([flank, H, SHELL_T], [off, H / 2, ez], 0),
    box([doorW, H - headB, SHELL_T], [0, (H + headB) / 2, ez], 0),
    // 2026-07-30 legibility pass ("can't tell if buildings have doors"): the
    // doorway READS from the street — a bold rust-accent frame, a proud
    // lintel bar and a small awning hood. ⚠ All frame boxes are EXTERIOR-
    // proud only (inner edges buried inside the wall body): an interior-side
    // protrusion sat a few cm off the flank faces and flashed against them
    // ("clipping on the part facing the INSIDE of the door" play report).
    // ⚠ NO frame face may share a plane with the flank/header boxes: the
    // verticals' inner side faces sat EXACTLY on the flank boundary planes
    // (x = ±doorW/2) and the lintel's underside exactly on the header's
    // bottom (y = headB) — same-orientation coplanar pairs, the door-jamb
    // z-fight. Offsets keep every frame face ≥ 0.02 off those planes.
    box([0.22, headB, SHELL_T], [-(doorW / 2 + 0.07), headB / 2, ez + 0.05], 2),
    box([0.22, headB, SHELL_T], [doorW / 2 + 0.07, headB / 2, ez + 0.05], 2),
    box([doorW + 0.44, 0.26, SHELL_T], [0, headB + 0.11, ez + 0.05], 2),
    box([doorW + 0.7, 0.1, 0.9], [0, headB + 0.55, ez + 0.35], 1, [0.28, 0, 0]), // awning hood
  ]);
}

/** Interior floor slab (one per level-0 cell); dark deck material. Authored
 *  1.4 m THICK with its local top at y = 0 — the bake places it at
 *  y = shell.floorTopM, sinking the excess into the ground, so ONE fixed doc
 *  serves every per-building floor raise (no y-scaling in the instancer).
 *  ⚠ INSET 0.025/side from the cell edge: a full-U slab's side faces sit
 *  EXACTLY COPLANAR with the wall panels' outer faces (both at the cell-edge
 *  plane, same orientation) and z-fight as dark flashes — worst along the
 *  door edges, where the slab's exposed riser fought the door flanks (the
 *  2026-07-30 "flash-clipping around the doors" play finding). The 5 cm
 *  seams between adjacent cells' slabs read as deck-plate panel joints. */
function shellFloor(): KitEntry {
  nextId = 0;
  const U = MODULE_UNIT_M - 0.05;
  return doc("shell-floor", "Shell Floor Slab", [1, 1], "structure", [
    box([U, 1.4, U], [0, -0.7, 0], 1),
  ]);
}

/** P3: upper-storey floor slab — THIN (0.3 m, matching its collision box): it
 *  is simultaneously level L's floor and level L−1's CEILING, so a thick slab
 *  would eat the lower room's headroom. Local top at y = 0 (place at L·H);
 *  inset off the wall planes like shell-floor. */
function shellFloorUpper(): KitEntry {
  nextId = 0;
  const U = MODULE_UNIT_M - 0.05;
  return doc("shell-floor-upper", "Shell Upper Floor", [1, 1], "structure", [
    box([U, 0.3, U], [0, -0.15, 0], 1),
  ]);
}

/** Roof cap for exposed hollow cells — the structureBox cap, standalone. */
function shellRoof(): KitEntry {
  nextId = 0;
  const U = MODULE_UNIT_M, H = LEVEL_H_M;
  return doc("shell-roof", "Shell Roof Cap", [1, 1], "structure", [
    box([U + 0.12, 0.2, U + 0.12], [0, H - 0.04, 0], 4),
    // interior ceiling liner: closes the view up into the cap's underside gap.
    // Inset like shell-floor — full-U made it coplanar with the wall outer
    // faces (the dark slashes along the wall tops in the same play finding).
    box([U - 0.05, 0.1, U - 0.05], [0, H - SHELL_T + 0.05, 0], 1),
  ]);
}

// ── interiors P2: furniture pieces ────────────────────────────────────────────
// Placed by interior.ts::interiorPlan on the raised floor slab (bake offsets
// them by shell.floorTopM). Local frame: +z = the piece's FACING (away from
// its wall), origin at the floor. Collision is NOT derived from these docs —
// interiorPlan emits its own per-piece boxes — so visual cylinders are fine.
//
// FURNITURE PALETTE (2026-07-30 colour pass — "furniture should have colour"):
// furniture docs carry their OWN materials, warmer and more varied than the
// architectural palette. The bake layers a per-BUILDING furniture tint on top
// (buildingMeshes FURN_TINTS) so no two houses' fittings read identical.
//   0 warm wood · 1 metal · 2 fabric/accent · 3 glass (night-emissive path)
//   4 dark wood · 5 linen · 6 foliage
const FURN_PALETTE = [
  { color: 0x8a6248, roughness: 0.85, metalness: 0.0 },  // 0 warm wood
  { color: 0x6b6259, roughness: 0.6, metalness: 0.4 },   // 1 metal
  { color: 0xb0562a, roughness: 0.85, metalness: 0.0 },  // 2 fabric / accent
  { color: 0x223038, roughness: 0.2, metalness: 0.0 },   // 3 glass
  { color: 0x54402e, roughness: 0.85, metalness: 0.0 },  // 4 dark wood
  { color: 0xd8cdb4, roughness: 0.9, metalness: 0.0 },   // 5 linen
  { color: 0x5d7d43, roughness: 0.9, metalness: 0.0 },   // 6 foliage
];
function furniture(id: string, name: string, nodes: AssetNode[]): KitEntry {
  nextId = 0;
  return doc(id, name, [1, 1], "greeble", nodes, FURN_PALETTE);
}

const FURNITURE: KitEntry[] = [
  furniture("int-bed", "Bed", [
    box([2.0, 0.35, 1.1], [0, 0.175, 0], 0),               // wood frame
    box([1.9, 0.16, 1.0], [0, 0.43, 0], 5),                // linen mattress
    box([1.1, 0.1, 1.02], [0.35, 0.53, 0], 2),             // fabric blanket (foot +x)
    box([0.55, 0.1, 0.7], [-0.6, 0.56, 0], 5),             // pillow (head −x)
    box([0.08, 0.85, 1.1], [-0.99, 0.425, 0], 0),          // headboard
  ]),
  furniture("int-table", "Table", [
    box([1.6, 0.08, 1.0], [0, 0.86, 0], 0),
    mods(box([0.08, 0.82, 0.08], [0.68, 0.41, 0.38], 4),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
  ]),
  furniture("int-stool", "Stool", [
    box([0.42, 0.07, 0.42], [0, 0.5, 0], 0),
    mods(box([0.06, 0.47, 0.06], [0.15, 0.235, 0.15], 4),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
  ]),
  furniture("int-bench", "Bench", [
    box([1.5, 0.07, 0.42], [0, 0.5, 0], 0),
    mods(box([0.08, 0.47, 0.34], [0.6, 0.235, 0], 4), [{ type: "mirror", axis: "x" }]),
  ]),
  furniture("int-counter", "Counter", [
    box([2.4, 0.95, 0.66], [0, 0.475, 0], 4),              // dark wood body
    box([2.56, 0.07, 0.82], [0, 1.0, 0], 0),               // warm wood top
    box([2.4, 0.12, 0.05], [0, 0.35, 0.36], 2),            // accent kick rail
  ]),
  furniture("int-shelf", "Shelf", [
    mods(box([0.07, 2.1, 0.45], [0.86, 1.05, 0], 0), [{ type: "mirror", axis: "x" }]),
    box([1.8, 0.06, 0.45], [0, 0.55, 0], 0),
    box([1.8, 0.06, 0.45], [0, 1.15, 0], 0),
    box([1.8, 0.06, 0.45], [0, 1.75, 0], 0),
    box([0.5, 0.3, 0.35], [-0.35, 0.72, 0], 2),            // stacked goods
    box([0.4, 0.25, 0.3], [0.4, 1.3, 0], 5),
  ]),
  furniture("int-cabinet", "Cabinet", [
    box([1.2, 1.7, 0.55], [0, 0.85, 0], 4),
    box([0.04, 0.5, 0.06], [0.25, 0.9, 0.29], 1),          // handle
    box([1.28, 0.06, 0.6], [0, 1.73, 0], 0),               // cap
  ]),
  furniture("int-hearth", "Hearth", [
    box([1.6, 0.5, 0.85], [0, 0.25, 0], 1),                // stone/metal plinth
    box([1.3, 1.3, 0.65], [0, 1.05, -0.08], 5),            // plastered breast
    box([0.7, 0.55, 0.1], [0, 0.55, 0.3], 2),              // ember glow face
    box([0.8, 2.2, 0.5], [0, 2.6, -0.15], 5),              // flue up the wall
  ]),
  furniture("int-rug", "Rug", [
    box([2.6, 0.04, 1.9], [0, 0.02, 0], 2),
    box([2.2, 0.045, 1.5], [0, 0.022, 0], 5),              // inner linen field
  ]),
  furniture("int-lamp", "Standing Lamp", [
    cyl(0.16, 0.06, [0, 0.03, 0], 1),
    cyl(0.045, 1.6, [0, 0.83, 0], 1),
    box([0.32, 0.36, 0.32], [0, 1.8, 0], 3),               // glass head (night-emissive)
  ]),
  furniture("int-barrel", "Barrel", [
    cyl(0.42, 0.95, [0, 0.475, 0], 0),
    cyl(0.44, 0.08, [0, 0.25, 0], 1),
    cyl(0.44, 0.08, [0, 0.72, 0], 1),
  ]),
  // ── P-B4 workplace pieces (2026-07-31): a smithy is not a bookshop ──
  furniture("int-forge", "Forge", [
    box([1.5, 1.0, 0.9], [0, 0.5, 0], 1),                  // stone/metal block
    box([0.9, 0.35, 0.6], [0, 1.1, -0.05], 2),             // ember bed (accent glow)
    box([1.1, 1.6, 0.7], [0, 2.1, -0.1], 4),               // chimney hood
    box([0.8, 1.2, 0.5], [0, 3.3, -0.1], 4),               // flue
  ]),
  furniture("int-anvil", "Anvil", [
    box([0.5, 0.45, 0.5], [0, 0.225, 0], 0),               // stump
    box([0.85, 0.28, 0.3], [0, 0.6, 0], 1),                // anvil body
    box([0.3, 0.14, 0.22], [0.45, 0.62, 0], 1),            // horn block
  ]),
  furniture("int-workbench", "Workbench", [
    box([1.8, 0.09, 0.75], [0, 0.85, 0], 0),               // top
    mods(box([0.09, 0.81, 0.09], [0.78, 0.4, 0.28], 4),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
    box([0.4, 0.18, 0.3], [-0.5, 0.98, 0.05], 1),          // vise/tool block
    box([0.55, 0.1, 0.35], [0.45, 0.95, -0.08], 2),        // parts tray
  ]),
  furniture("int-stall", "Market Stall", [
    box([2.0, 0.9, 0.7], [0, 0.45, 0], 4),                 // stall body
    box([2.15, 0.07, 0.85], [0, 0.95, 0], 0),              // top board
    mods(box([0.09, 1.9, 0.09], [0.95, 0.95, 0.3], 0), [{ type: "mirror", axis: "x" }]),
    box([2.2, 0.09, 0.9], [0, 1.95, 0.12], 2, [0.18, 0, 0]), // awning board
    box([0.5, 0.25, 0.4], [-0.4, 1.1, 0], 5),              // goods
    box([0.4, 0.2, 0.35], [0.45, 1.08, 0.05], 6),
  ]),
  furniture("int-keg", "Keg Rack", [
    box([1.4, 0.16, 0.8], [0, 0.08, 0], 4),                // cradle base
    cyl(0.34, 1.2, [0, 0.5, -0.12], 0, [0, 0, 1.5707963267948966]),  // keg (side-on)
    cyl(0.3, 1.1, [0, 1.1, -0.02], 0, [0, 0, 1.5707963267948966]),   // upper keg
    box([0.12, 0.12, 0.1], [0.35, 0.5, 0.24], 1),          // tap
  ]),
  furniture("int-desk", "Writing Desk", [
    box([1.5, 0.08, 0.8], [0, 0.82, 0], 4),                // top
    box([0.5, 0.74, 0.7], [-0.45, 0.37, 0], 4),            // drawer stack
    mods(box([0.08, 0.78, 0.08], [0.66, 0.39, 0.3], 4), [{ type: "mirror", axis: "z" }]),
    box([0.45, 0.03, 0.32], [0.2, 0.87, 0], 5),            // papers
  ]),
  furniture("int-cauldron", "Cauldron", [
    box([0.9, 0.25, 0.9], [0, 0.125, 0], 1),               // stand
    cyl(0.42, 0.55, [0, 0.55, 0], 1),                      // pot
    cyl(0.36, 0.08, [0, 0.86, 0], 6),                      // brew surface
  ]),
  // ── interiors wave 2 (2026-07-31): barracks / works / sanctum props ──
  furniture("int-rack", "Weapon Rack", [
    box([1.6, 1.9, 0.18], [0, 0.95, 0], 4),                // back board
    box([1.7, 0.09, 0.3], [0, 0.35, 0.08], 0),             // lower shelf
    box([0.07, 1.3, 0.07], [-0.5, 1.1, 0.1], 1, [0, 0, 0.12]), // stacked arms
    box([0.07, 1.3, 0.07], [0, 1.1, 0.1], 1, [0, 0, -0.06]),
    box([0.07, 1.3, 0.07], [0.5, 1.1, 0.1], 1, [0, 0, 0.1]),
  ]),
  furniture("int-machine", "Works Machine", [
    box([1.9, 1.2, 1.0], [0, 0.6, 0], 1),                  // machine body
    cyl(0.45, 0.5, [0.55, 1.45, 0], 1, [1.5707963267948966, 0, 0]), // drum
    box([0.5, 0.9, 0.5], [-0.6, 1.55, 0], 4),              // hopper
    box([0.16, 0.5, 0.16], [0.2, 1.45, 0.3], 2),           // control block (accent)
  ]),
  furniture("int-altar", "Altar", [
    box([1.9, 0.35, 0.9], [0, 0.175, 0], 1),               // stone step
    box([1.4, 0.75, 0.65], [0, 0.72, 0], 5),               // altar block
    box([1.5, 0.09, 0.75], [0, 1.14, 0], 4),               // top slab
    box([0.22, 0.6, 0.22], [0, 1.5, 0], 2),                // votive flame (accent)
  ]),

  furniture("int-lectern", "Lectern", [
    box([0.5, 0.1, 0.5], [0, 0.05, 0], 4),
    box([0.14, 1.05, 0.14], [0, 0.6, 0], 4),
    box([0.55, 0.06, 0.45], [0, 1.18, 0.06], 2, [0.35, 0, 0]),
  ]),

  // ── 2026-07-30 room-variety additions ──
  furniture("int-chest", "Chest", [
    box([0.9, 0.42, 0.52], [0, 0.21, 0], 0),
    box([0.94, 0.12, 0.56], [0, 0.48, 0], 4),              // lid
    box([0.1, 0.2, 0.05], [0, 0.32, 0.28], 1),             // clasp
  ]),
  furniture("int-nightstand", "Nightstand", [
    box([0.5, 0.52, 0.45], [0, 0.26, 0], 4),
    box([0.54, 0.05, 0.49], [0, 0.545, 0], 0),
    box([0.2, 0.16, 0.2], [0, 0.65, 0], 2),                // candle jar
  ]),
  furniture("int-stove", "Stove", [
    box([0.9, 0.85, 0.6], [0, 0.425, 0], 1),
    box([0.5, 0.4, 0.06], [0, 0.42, 0.31], 2),             // fire door
    cyl(0.09, 1.6, [0.25, 1.6, -0.15], 1),                 // flue pipe
  ]),
  furniture("int-basin", "Wash Basin", [
    box([0.6, 0.7, 0.5], [0, 0.35, 0], 0),
    cyl(0.26, 0.16, [0, 0.78, 0], 5),                      // bowl
  ]),
  furniture("int-plant", "Potted Plant", [
    cyl(0.2, 0.32, [0, 0.16, 0], 2),
    cone(0.34, 0.75, [0, 0.75, 0], 6),                     // foliage
    sphere(0.2, [0, 1.12, 0], 6),
  ]),
  furniture("int-bookshelf", "Bookshelf", [
    mods(box([0.07, 2.0, 0.4], [0.8, 1.0, 0], 4), [{ type: "mirror", axis: "x" }]),
    box([1.65, 0.06, 0.4], [0, 0.5, 0], 4),
    box([1.65, 0.06, 0.4], [0, 1.1, 0], 4),
    box([1.65, 0.06, 0.4], [0, 1.7, 0], 4),
    box([1.4, 0.4, 0.3], [0, 0.73, 0], 2),                 // book rows (fabric)
    box([1.2, 0.4, 0.3], [-0.1, 1.33, 0], 6),
    box([0.9, 0.36, 0.3], [0.2, 1.91, 0], 5),
  ]),
];

/** Interior stair (P3): CHUNKY STEPS as a SOLID ascending mass (2026-07-30
 *  play calls — first "steps not a ramp", then "two layers of blocks": the
 *  arrayed-tread + understructure pair read as duplicated boxes). Ten
 *  explicit COLUMNS, each from the floor up to its own step top — one box
 *  per step, nothing floating, nothing doubled. Anchored at the STAIR
 *  CENTRELINE (plan.ts ShellStair), local +z = ascent; the collision tread
 *  line (18 × 0.222 m) tracks the step diagonal to ±0.1 m. */
function intStair(): KitEntry {
  nextId = 0;
  // Run 3.6 shifted +0.2 along the ascent — matches the plan.ts collision
  // treads (SHELL_STAIR_RUN_M/OFFSET_M): the top step lands FLUSH with the
  // arrival edge, no gap over the stairwell opening.
  const H = LEVEL_H_M, RUN = 3.6, OFF = 0.2, N = 10;
  const riser = H / N, depth = RUN / N;
  const steps: AssetNode[] = [];
  for (let k = 0; k < N; k++) {
    const top = (k + 1) * riser;
    steps.push(box([1.2, top, depth], [0, top / 2, OFF - RUN / 2 + (k + 0.5) * depth], 1));
  }
  return doc("int-stair", "Interior Stair", [1, 1], "greeble", steps);
}

// ── 2026-07-30: projecting KIND SIGNS beside doors ───────────────────────────
// A bracket arm + hanging board + a per-trade icon, anchored on the door wall
// beside the doorway (bake places them; +z = out of the wall). Icon reads the
// building's USE: mug = inn, coin = market/depot, hammer = smithy/workshop,
// flask = apothecary, emblem = civic. FURN palette (wood board, coloured
// icons) + the per-building furniture tint in the bake.
function sign(id: string, name: string, icon: AssetNode[]): KitEntry {
  nextId = 0;
  return doc(id, name, [1, 1], "greeble", [
    box([0.07, 0.07, 0.95], [0, 0.45, 0.48], 1),        // bracket arm
    box([0.05, 0.5, 0.05], [0, 0.2, 0.05], 1),          // wall stay
    box([0.8, 0.8, 0.07], [0, -0.18, 0.6], 4),          // hanging board
    ...icon,
  ], FURN_PALETTE);
}

const SIGNS: KitEntry[] = [
  sign("sign-mug", "Tavern Sign", [
    cyl(0.15, 0.34, [0, -0.16, 0.66], 5),
    box([0.07, 0.2, 0.06], [0.2, -0.16, 0.66], 5),
  ]),
  sign("sign-coin", "Trade Sign", [
    cyl(0.2, 0.07, [0, -0.16, 0.66], 5, [1.5707963267948966, 0, 0]),
    box([0.1, 0.1, 0.08], [0, -0.16, 0.68], 2),
  ]),
  sign("sign-hammer", "Craft Sign", [
    box([0.32, 0.15, 0.1], [0, -0.02, 0.66], 1),
    box([0.07, 0.34, 0.08], [0, -0.26, 0.66], 0),
  ]),
  sign("sign-flask", "Apothecary Sign", [
    sphere(0.14, [0, -0.26, 0.66], 6),
    cyl(0.05, 0.16, [0, -0.1, 0.66], 6),
  ]),
  sign("sign-emblem", "Civic Sign", [
    box([0.27, 0.27, 0.08], [0, -0.16, 0.66], 2, [0, 0, 0.7853981633974483]),
  ]),
  // ── 2026-07-31 style pass: every workplace type carries its trade sign ──
  sign("sign-sword", "Garrison Sign", [
    box([0.08, 0.4, 0.06], [0, -0.18, 0.66], 1),          // blade
    box([0.24, 0.06, 0.07], [0, -0.02, 0.66], 0),         // crossguard
  ]),
  sign("sign-sheaf", "Farm Sign", [
    box([0.07, 0.34, 0.06], [-0.08, -0.16, 0.66], 6, [0, 0, 0.25]),
    box([0.07, 0.34, 0.06], [0, -0.14, 0.67], 6),
    box([0.07, 0.34, 0.06], [0.08, -0.16, 0.66], 6, [0, 0, -0.25]),
  ]),
  sign("sign-crate", "Storage Sign", [
    box([0.3, 0.3, 0.07], [0, -0.16, 0.66], 0),
    box([0.32, 0.06, 0.08], [0, -0.16, 0.665], 1),
  ]),
  sign("sign-flame", "Sanctum Sign", [
    box([0.16, 0.3, 0.06], [0, -0.14, 0.66], 2, [0, 0, 0.7853981633974483]),
    box([0.1, 0.18, 0.05], [0, -0.02, 0.67], 5),
  ]),
  sign("sign-book", "Academy Sign", [
    box([0.32, 0.24, 0.07], [0, -0.16, 0.66], 5),
    box([0.03, 0.24, 0.08], [0, -0.16, 0.665], 4),        // spine
  ]),
  sign("sign-mask", "Leisure Sign", [
    box([0.26, 0.3, 0.07], [0, -0.16, 0.66], 5),
    box([0.06, 0.06, 0.05], [-0.06, -0.1, 0.7], 4),       // eyes
    box([0.06, 0.06, 0.05], [0.06, -0.1, 0.7], 4),
    box([0.14, 0.04, 0.05], [0, -0.26, 0.7], 4),          // mouth
  ]),
  sign("sign-anchor", "Harbor Sign", [
    box([0.07, 0.34, 0.06], [0, -0.16, 0.66], 1),
    box([0.26, 0.06, 0.07], [0, -0.3, 0.66], 1),
    box([0.16, 0.06, 0.06], [0, -0.02, 0.66], 1),
  ]),
];

// ── 2026-07-31 style pass: per-type door/roof MOTIFS ─────────────────────────
// Small authored pieces the bake plants deterministically on type families:
// a glowing spire finial crowns faith buildings, a lantern lights inn doors.
const MOTIFS: KitEntry[] = [
  greeble("mot-finial", "Roof Finial", [
    box([0.28, 0.5, 0.28], [0, 0.25, 0], 1),              // base plinth
    box([0.1, 1.7, 0.1], [0, 1.3, 0], 1),                 // spire
    box([0.2, 0.2, 0.2], [0, 2.3, 0], 3, [0, 0, 0.7853981633974483]), // glow tip (glass)
  ]),
  greeble("mot-lantern", "Door Lantern", [
    box([0.06, 0.5, 0.06], [0, 0.25, 0.02], 1),           // bracket stay
    box([0.32, 0.06, 0.06], [0, 0.5, 0.16], 1),           // arm
    box([0.16, 0.22, 0.16], [0, 0.34, 0.28], 3),          // glass lantern body
    box([0.2, 0.05, 0.2], [0, 0.48, 0.28], 1),            // cap
  ]),
];

// ── WALL ART (2026-07-30 "fun procedural wall art for homes") ─────────────────
// Framed canvases hung on interior walls. Each variant is COMPOSED here from a
// tiny seeded grammar (horizon / stripes / blocks / diamonds) — procedural at
// authoring time, deterministic forever after. The doc carries its own hang
// height (frame bottom ≈ 1.15 m) so the bake places art like any furniture
// piece with NO height special-case; +z faces into the room (wall-slot rot).
// Palette is art's own EXCEPT index 3, which must stay the universal glass pin.
const ART_PALETTE = [
  { color: 0x54402e, roughness: 0.85, metalness: 0.0 },  // 0 frame wood
  { color: 0xe8e0cc, roughness: 0.95, metalness: 0.0 },  // 1 canvas cream
  { color: 0xc4482e, roughness: 0.9, metalness: 0.0 },   // 2 vermilion
  { color: 0x223038, roughness: 0.2, metalness: 0.0 },   // 3 glass (universal pin)
  { color: 0x2e4f78, roughness: 0.9, metalness: 0.0 },   // 4 deep blue
  { color: 0xd9a441, roughness: 0.9, metalness: 0.0 },   // 5 ochre
  { color: 0x5d7d43, roughness: 0.9, metalness: 0.0 },   // 6 green
  { color: 0x2a2a35, roughness: 0.9, metalness: 0.0 },   // 7 ink
];
const ART_HUES = [2, 4, 5, 6, 7]; // paint colours (never frame/canvas/glass)
function artPiece(i: number): KitEntry {
  nextId = 0;
  // Tiny deterministic LCG per variant — kit docs must be pure f(nothing).
  let s = (i * 2654435761 + 0x9e3779b9) >>> 0;
  const rnd = (): number => ((s = (s * 1664525 + 1013904223) >>> 0) / 0x100000000);
  const pick = (): number => ART_HUES[Math.floor(rnd() * ART_HUES.length)];
  const W = 0.9 + rnd() * 0.6, Hh = 0.65 + rnd() * 0.35;
  const y0 = 1.15, cy = y0 + Hh / 2;
  const nodes: AssetNode[] = [
    box([W + 0.12, 0.06, 0.06], [0, y0 - 0.03, 0.03], 0),        // frame bottom
    box([W + 0.12, 0.06, 0.06], [0, y0 + Hh + 0.03, 0.03], 0),   // frame top
    box([0.06, Hh, 0.06], [-(W + 0.06) / 2, cy, 0.03], 0),       // frame left
    box([0.06, Hh, 0.06], [(W + 0.06) / 2, cy, 0.03], 0),        // frame right
    box([W, Hh, 0.04], [0, cy, 0.02], 1),                        // canvas
  ];
  const el = (w: number, h: number, x: number, y: number, mat: number,
    rz = 0): void => { nodes.push(box([w, h, 0.04], [x, y, 0.045], mat, [0, 0, rz])); };
  const style = i % 4;
  if (style === 0) {
    // Horizon: sky field, tilted sun square, land band, mountain diamond.
    el(W - 0.08, Hh * 0.55, 0, y0 + Hh * 0.66, 4);
    el(Hh * 0.22, Hh * 0.22, (rnd() - 0.5) * W * 0.5, y0 + Hh * 0.7, 5, Math.PI / 4);
    el(W - 0.08, Hh * 0.3, 0, y0 + Hh * 0.19, pick());
    el(Hh * 0.3, Hh * 0.3, (rnd() - 0.5) * W * 0.6, y0 + Hh * 0.4, 7, Math.PI / 4);
  } else if (style === 1) {
    // Stripes: 4-6 vertical bands.
    const n = 4 + Math.floor(rnd() * 3);
    const bw = (W - 0.08) / n;
    for (let k = 0; k < n; k++) {
      el(bw - 0.02, Hh - 0.08, -(W - 0.08) / 2 + (k + 0.5) * bw, cy, pick());
    }
  } else if (style === 2) {
    // Blocks: an off-centre split into three fields + ink dividers.
    const fx = 0.25 + rnd() * 0.5, fy = 0.3 + rnd() * 0.4;
    const lw = (W - 0.08) * fx, rw = (W - 0.08) - lw;
    el(lw - 0.02, Hh - 0.08, -(W - 0.08) / 2 + lw / 2, cy, pick());
    el(rw - 0.02, (Hh - 0.08) * fy, (W - 0.08) / 2 - rw / 2, y0 + 0.04 + (Hh - 0.08) * fy / 2, pick());
    el(rw - 0.02, (Hh - 0.08) * (1 - fy) - 0.02, (W - 0.08) / 2 - rw / 2, y0 + Hh - 0.04 - (Hh - 0.08) * (1 - fy) / 2, pick());
    nodes.push(box([0.03, Hh - 0.08, 0.04], [-(W - 0.08) / 2 + lw, cy, 0.055], 7));
    nodes.push(box([rw - 0.02, 0.03, 0.04], [(W - 0.08) / 2 - rw / 2, y0 + 0.04 + (Hh - 0.08) * fy, 0.055], 7));
  } else {
    // Diamonds: a loose grid of tilted squares on the canvas.
    const cols = 2 + Math.floor(rnd() * 2), rows = 2;
    for (let gx = 0; gx < cols; gx++) for (let gy = 0; gy < rows; gy++) {
      const d = Hh * (0.16 + rnd() * 0.1);
      el(d, d, -(W - 0.3) / 2 + (gx + 0.5) * ((W - 0.3) / cols),
        y0 + 0.1 + (gy + 0.5) * ((Hh - 0.2) / rows), pick(), Math.PI / 4);
    }
  }
  return doc(`art-${i}`, `Wall Art ${i}`, [1, 1], "greeble", nodes, ART_PALETTE);
}
const ART: KitEntry[] = Array.from({ length: 8 }, (_, i) => artPiece(i));

/** Trim piece factory — decorative, no sockets required. */
function trim(id: string, name: string, nodes: AssetNode[]): KitEntry {
  nextId = 0;
  return doc(id, name, [1, 1], "trim", nodes);
}

/** Greeble factory — small rooftop/facade props, no sockets required. */
function greeble(id: string, name: string, nodes: AssetNode[]): KitEntry {
  nextId = 0;
  return doc(id, name, [1, 1], "greeble", nodes);
}

const ENTRIES: KitEntry[] = [
  // ── structure modules ──
  structureBox("mod-box", "Plain Module", 1, 1),
  structureBox("mod-box-win", "Window Module", 1, 1, [
    // window band, +z face — proportional to the module (1.8×0.9 @ unit 3)
    box([MODULE_UNIT_M * 0.6, LEVEL_H_M * 0.3, 0.12],
      [0, LEVEL_H_M * 0.63, (MODULE_UNIT_M - 0.1) / 2 + 0.06], 3),
  ]),
  structureBox("mod-box-door", "Door Module", 1, 1, [
    box([1.2, 2.1, 0.14], [0, 1.05, (MODULE_UNIT_M - 0.1) / 2 + 0.07], 1), // door panel, +z face
  ]),
  doorHollow(),
  structureBox("mod-hall", "Hall Module", 2, 1),
  // armoured hull-front read: plating leans in as it rises (taper t=1 at the
  // TOP per proceduralAssetView applyDeforms, so factor 0.8 = top 0.8× base)
  structureBox("mod-hull", "Hull-Front Module", 1, 1, [],
    [{ type: "taper", axis: "y", factor: 0.8 }]),
  towerShaft(),
  towerHead(),
  dome(),

  // ── interiors P1: hollow ground-floor shell ──
  shellWall("shell-wall", "Shell Wall"),
  shellWallWin(),
  shellWallDoor(),
  shellFloor(),
  shellFloorUpper(),
  shellRoof(),
  intStair(),

  // ── interiors P2: furniture ──
  ...FURNITURE,

  // ── door kind signs ──
  ...SIGNS,

  // ── per-type door/roof motifs (2026-07-31 style pass) ──
  ...MOTIFS,

  // ── wall art (2026-07-30 "fun procedural wall art for homes") ──
  ...ART,

  // ── trims ──
  trim("trim-parapet-edge", "Parapet Edge", [
    box([MODULE_UNIT_M, 0.5, 0.22], [0, 0.25, MODULE_UNIT_M / 2 - 0.11], 1),
  ]),
  trim("trim-parapet-corner", "Parapet Corner", [
    box([0.5, 0.6, 0.5], [0, 0.3, 0], 1),
  ]),
  trim("trim-skirt", "Rubble Skirt", [
    mods(box([MODULE_UNIT_M, 0.5, 0.4], [0, 0.25, 0], 1), [{ type: "taper", axis: "y", factor: 0.5 }]),
  ]),
  trim("trim-stair", "Ramp Stair", [
    box([1.2, LEVEL_H_M, 0.3], [0, LEVEL_H_M / 2, 0], 1, [0.62, 0, 0]),
  ]),

  // ── greebles ──
  greeble("grb-tank-s", "Small Tank", [
    cyl(0.5, 1.2, [0, 0.6, 0], 1),
    cyl(0.52, 0.15, [0, 0.75, 0], 2), // accent band
  ]),
  greeble("grb-tank-l", "Large Tank", [
    cyl(0.9, 2.2, [0, 1.1, 0], 1),
    cyl(0.92, 0.2, [0, 1.5, 0], 2),
  ]),
  greeble("grb-antenna", "Antenna Array", [
    mods(box([0.08, 1.6, 0.08], [-0.22, 0.8, 0], 1),
      [{ type: "array", mode: "linear", count: 3, offset: [0.22, 0.2, 0] }]),
  ]),
  greeble("grb-dish", "Satellite Dish", [
    box([0.1, 1.0, 0.1], [0, 0.5, 0], 1),               // post
    cone(0.7, 0.35, [0, 1.1, 0], 1, [0.6, 0, 0]),       // tilted dish
  ]),
  greeble("grb-awning", "Awning", [
    box([2.4, 0.08, 1.4], [0, 1.4, 0.5], 2, [0.35, 0, 0]),
    mods(box([0.1, 1.4, 0.1], [1.0, 0.7, -0.1], 1), [{ type: "mirror", axis: "x" }]), // supports
  ]),
  greeble("grb-vent", "Roof Vent", [
    box([0.8, 0.4, 0.8], [0, 0.2, 0], 1),
    box([0.5, 0.25, 0.5], [0, 0.55, 0], 1),
  ]),
  greeble("grb-crate", "Crate Stack", [
    box([0.8, 0.8, 0.8], [0, 0.4, 0], 2),
    box([0.7, 0.7, 0.7], [0.6, 0.35, 0.2], 2),
    box([0.6, 0.6, 0.6], [-0.2, 0.3, 0.5], 2),
  ]),
  // Rebuilt 2026-07-30 ("banners clip into ceiling parts / should stick out"):
  // a PROJECTING bracket banner — a horizontal arm out of the wall with the
  // cloth HANGING below it. Anchored at a wall socket (mid-wall height, +z =
  // out of the wall): everything extends OUTWARD and DOWNWARD, so it can
  // never reach the eaves/cap overhang above.
  greeble("grb-banner", "Banner", [
    box([0.07, 0.07, 0.9], [0, 0.55, 0.45], 1),          // bracket arm (out of the wall)
    box([0.05, 0.55, 0.05], [0, 0.28, 0.05], 1),         // wall stay
    box([0.06, 1.5, 0.72], [0, -0.25, 0.5], 2),          // hanging cloth (below the arm)
    box([0.1, 0.08, 0.78], [0, 0.51, 0.5], 1),           // hanger rail
  ]),
  greeble("grb-watertower", "Water Tower", [
    mods(box([0.14, 1.6, 0.14], [0.6, 0.8, 0.6], 1),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]), // 4 legs
    cyl(0.85, 1.8, [0, 2.5, 0], 1),                     // tank, top ≈ 3.4 m
    cyl(0.87, 0.22, [0, 2.9, 0], 2),                    // accent band
  ]),
];

export const BUILDING_KIT: ReadonlyMap<string, KitEntry> =
  new Map(ENTRIES.map((e) => [e.id, e]));
